/**
 *
 * BranchDetailsForm
 *
 */
import { City, Country, Language } from 'core/utils/types/modules/org';
import React, { memo, useCallback } from 'react';
import { Input } from 'core/components/Input';
import { Button } from 'core/components/Button';
import { RootState } from 'controller/types';
import { useDispatch, useSelector } from 'react-redux';
import { useBranchSetupSlice } from 'features/BranchSetup/slice';
import { useAuthenticationSlice } from 'features/Authentication/slice';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useParams } from 'react-router-dom';
import { DropDown } from 'libs/DropDown';
import { Field, Form, Formik, FormikProps } from 'formik';
import { PhotoUrl } from 'core/components/SetupComponents/PhotoUrl';
import * as Yup from 'yup';
import { useFetchLanguageList } from 'features/BranchSetup/hooks/useFetchLanguageList';
import { useFetchCity } from 'features/BranchSetup/hooks/useFetchCity';
import { isRequiredMobileNumber, isRequiredNumberGreaterThan0, isRequiredString } from 'core/utils/validationSchemas';
import { MobileComponent } from 'core/components/SetupComponents/MobileComponent';
import { useFetchCountryCode } from 'features/BranchSetup/hooks/useFetchCountryCode';
import MapField from 'core/components/MapField';

interface Props {
  values?: any;
  action?: string;
  onNext?: () => void
}

const DEFAULT_GEOFENCE_RADIUS_METERS = 300;

export const BranchDetailsForm = memo((props: Props) => {
  const state = useSelector((state: RootState) => state.branchSetup);
  const { actions: authenticationActions } = useAuthenticationSlice();
  const authData = useAuth();
  const dispatch = useDispatch();
  const { actions } = useBranchSetupSlice();
  const params: any = useParams();
  const [cityList, setcityList] = React.useState<City[]>([]);
  const [languageList, setlanguageList] = React.useState<Language[]>([]);
  const [initialValues, setinitialValues] = React.useState<any>(undefined);
  const [selectedFile, setSelectedFile] = React.useState<File[]>();
  const [selectedPrincipalSignFile, setSelectedPrincipalSignFile] = React.useState<File[]>();
  const [countryList, setcountryList] = React.useState<Country[]>([]);
  const latestFormValuesRef = React.useRef<any>();

  useFetchLanguageList(languageList, setlanguageList);
  useFetchCity(cityList, setcityList);
  useFetchCountryCode(countryList, setcountryList);

  // Helper function to remove +91 prefix
  const removeCountryCode = (number: string) => {
    if (!number) return number;
    return number.startsWith('+91') ? number.substring(3) : number;
  };

  // Helper function to add +91 prefix if not present
  const addCountryCode = (number: string) => {
    if (!number || number.trim() === '') return number;
    return number.startsWith('+91') ? number : `+91${number}`;
  };

  React.useEffect(() => {
    if (!!props?.values?.BRANCH_ID) {
      const { STATUS, ...reset } = props?.values;
      
      // Remove +91 from ADMISSION_COUNSELLOR_NUMBER during prefill
      if (reset.ADMISSION_COUNSELLOR_NUMBER) {
        reset.ADMISSION_COUNSELLOR_NUMBER = removeCountryCode(reset.ADMISSION_COUNSELLOR_NUMBER);
      }

      reset.LATITUDE = Number(reset.LATITUDE || 0);
      reset.LONGITUDE = Number(reset.LONGITUDE || 0);
      reset.GEOFENCE_RADIUS_METERS = Number(reset.GEOFENCE_RADIUS_METERS || DEFAULT_GEOFENCE_RADIUS_METERS);
      reset.GOOGLE_MAP_POSITION = {
        lat: reset.LATITUDE,
        lng: reset.LONGITUDE,
        address: reset.ADDRESS || '',
      };
      
      setinitialValues(initialValues => reset);
    }
  }, [props?.values?.BRANCH_ID]);

  React.useEffect(() => {
    if (state?.navigate) {
      props?.onNext && props?.onNext();
      dispatch(actions.setNavigate(false));
      const currentFormValues = latestFormValuesRef.current;
      dispatch(authenticationActions.updateAuthData({ ...authData, BRANCH_NAME: currentFormValues?.NAME, BRANCH_ADDRESS: currentFormValues?.ADDRESS }))
    }
  }, [state?.navigate])

  const SHOWFORM = Boolean(!!initialValues && !!cityList?.length && !!languageList?.length && !!countryList?.length);

  return SHOWFORM ? <Formik
    initialValues={initialValues}
    validationSchema={Yup.object().shape({
      NAME: isRequiredString('Name is a required Field'),
      LANGUAGE_ID: isRequiredNumberGreaterThan0('Language is a required Field'),
      PHONE: isRequiredMobileNumber('Mobile Number is a required Field'),
      FOUNDING_YEAR: Yup.string().matches(/^[0-9]{4}$/, 'Invalid founding year. Please enter a valid 4-digit founding year').required(),
      CITY_ID: isRequiredNumberGreaterThan0('City is a required Field'),
      EMAIL_ID: isRequiredString('Email is a required Field'),
      PINCODE: isRequiredString('Pincode is a required Field'),
      ADDRESS: isRequiredString('Address is a required Field'),
      SMS_BRANCH_NAME: isRequiredString('SMS Branch Name is a required Field').max(30, 'SMS Branch Name cannot exceed 30 characters'),
      // UPI_ID: isRequiredString('UPI ID is a required Field'),
      CONVENIENCE_FEE_TYPE: isRequiredString('Convenience Fee Type is a required Field'),
      LATITUDE: Yup.number().required('Latitude is a required Field'),
      LONGITUDE: Yup.number().required('Longitude is a required Field'),
      GEOFENCE_RADIUS_METERS: Yup.number()
        .required('Geofence Radius is a required Field')
        .min(1, 'Geofence Radius must be greater than 0'),
       ADMISSION_COUNSELLOR_NUMBER: Yup.string()
      .nullable()
      .test('is-valid-mobile', 'Mobile number must be exactly 10 digits', function(value) {
        if (!value || value.trim() === '') return true;
        const cleanValue = value.replace(/\D/g, ''); 
        return cleanValue.length === 10;
      })
    })}
    onSubmit={async values => {
      const __values = { ...values };
      const mapPosition = __values.GOOGLE_MAP_POSITION;
      latestFormValuesRef.current = values;

       if (__values.ADMISSION_COUNSELLOR_NUMBER) {
        const cleanNumber = String(__values.ADMISSION_COUNSELLOR_NUMBER).replace(/\D/g, '');
        if (cleanNumber.length === 10 && !String(__values.ADMISSION_COUNSELLOR_NUMBER).includes('+91')) {
          __values.ADMISSION_COUNSELLOR_NUMBER = `+91${cleanNumber}`;
        }
      }

      __values.LATITUDE = Number(__values.LATITUDE || mapPosition?.lat || 0);
      __values.LONGITUDE = Number(__values.LONGITUDE || mapPosition?.lng || 0);
      __values.GEOFENCE_RADIUS_METERS = Number(__values.GEOFENCE_RADIUS_METERS || DEFAULT_GEOFENCE_RADIUS_METERS);

      dispatch(
        actions.createBranchSetupStart({
          URL: params?.url as string || 'branch',
          data: [{
            ...__values,
            BRANCH_ID: authData?.BRANCH_ID as number,
            PRIMARY_USER_ID: 1,
            SECONDARY_USER_ID: 1,
            CONVENIENCE_FEE_RATE: __values?.CONVENIENCE_FEE_RATE || 0,
          }],
          action: props.action || 'save',
          message: props.action
            ? 'Updated Successfully!'
            : 'Created Successfully!',
        })
      )
    }}
  >
    {(formikProps: FormikProps<any>) => <Form>
      <div className="flex justify-between">
        <div className="w-[34%] pt-8 flex flex-col justify-center items-center">
          <Field name={`LOGO`} >
            {({ field, form, meta }) => (
              <PhotoUrl
                uploadToS3={!props.action ? true : false}
                size="w-60 h-60"
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                URL={formikProps.values?.LOGO as string}
                setField={(photoURL: string) => {
                  formikProps.setFieldValue('LOGO', photoURL);
                }}
                dest={"LOGO"}
                id={props?.values?.BRANCH_ID}
                validatePngOnly
              />
            )}
          </Field>

          <div>
            <h3 className='text-natural-700 font-bold mt-5 mb-3'>Principal Signature</h3>
            <Field name={`PRINCIPAL_SIGN`} >
              {({ field, form, meta }) => (
                <PhotoUrl
                  uploadToS3={!props.action ? true : false}
                  size="w-60 h-20"
                  selectedFile={selectedPrincipalSignFile}
                  setSelectedFile={setSelectedPrincipalSignFile}
                  URL={formikProps.values?.PRINCIPAL_SIGN as string}
                  setField={(photoURL: string) => {
                    formikProps.setFieldValue('PRINCIPAL_SIGN', photoURL);
                  }}
                  dest={"PRINCIPALSIGN"}
                  id={props?.values?.BRANCH_ID}
                  validatePngOnly
                />
              )}
            </Field>
          </div>
        </div>
        <div className="grid w-[66%]">
          <div className="flex justify-between items-center gap-3">
            <Field name={`NAME`} >
              {({ field, form, meta }) => (
                <Input
                  type="text"
                  label="Branch Name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter Text"
                  name={field.name}
                  onBlur={field.onBlur}
                  error={meta.touched && meta.error}
                  message={meta.touched && meta.error}
                  required
                />
              )}
            </Field>
            <Field name={`LANGUAGE_ID`}>
              {({ field, form, meta }) => (
                <DropDown
                  getOptionValue={(option: Language) =>
                    option.LANGUAGE_ID
                  }
                  getOptionLabel={(option: Language) =>
                    `${option.NAME}`
                  }
                  options={languageList}
                  placeholder="Select Language"
                  onChange={(item: Language) => {
                    form.setFieldValue(
                      `LANGUAGE_ID`,
                      item.LANGUAGE_ID,
                    );
                  }}
                  label={'Preferred Language'}
                  defaultValue={languageList?.find(
                    (item: any) =>
                      item.LANGUAGE_ID ===
                      formikProps.values?.LANGUAGE_ID,
                  )}
                  name={`LANGUAGE_ID`}
                  onBlur={field.onBlur}
                  error={
                    formikProps.touched.LANGUAGE_ID && formikProps.errors.LANGUAGE_ID
                  }
                  message={
                    formikProps.touched.LANGUAGE_ID && formikProps.errors.LANGUAGE_ID
                  }
                  required
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-12 justify-between items-center gap-3">
            <div className='col-span-6'>
              <MobileComponent
                countryCodeName="COUNTRY_CODE"
                numberName="PHONE"
                codeWidth={4}
                countryList={countryList}
                inputValue={formikProps?.values?.PHONE}
                label='Mobile No'
                defaultCCValue={
                  countryList
                  && (countryList.find(
                    item => item.COUNTRY_CODE == formikProps?.initialValues?.COUNTRY_CODE
                  ) ||
                    countryList?.find(item => item.COUNTRY_CODE == '+91'))

                }
                errorCC={
                  formikProps.touched.COUNTRY_CODE &&
                  formikProps.errors.COUNTRY_CODE
                }
                messageCC={
                  formikProps.touched.COUNTRY_CODE &&
                  formikProps.errors.COUNTRY_CODE
                }
                errorMobile={
                  formikProps.touched.PHONE &&
                  formikProps.errors.PHONE
                }
                messageMobile={
                  formikProps.touched.PHONE &&
                  formikProps.errors.PHONE
                }
              />
            </div>
            <div className='col-span-6'>
              <Field name={`ADMISSION_COUNSELLOR_NUMBER`}>
                {({ field, form, meta }) => (
                  <Input
                    // disabled={formikProps.values.SAME_WHATSAPP_NUMBER}
                    type="tel"
                    label={"Admission Counsellor No"}
                    value={field.value}
                    // onChange={field.onChange}
                     onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      form.setFieldValue(field.name, value);
                    }}
                    placeholder="Enter Admission Counsellor Number"
                    name={field.name}
                    onBlur={field.onBlur}
                    error={meta.touched && meta.error}
                    message={meta.touched && meta.error}
                    required={false}
                    maxLength={10}
                  />
                )}
              </Field>
            </div>
            <div className='col-span-6'>
              <Field name={`EMAIL_ID`}>
                {({ field, form, meta }) => (
                  <Input
                    type="email"
                    label="Branch Email ID"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter Email ID"
                    name={field.name}
                    onBlur={field.onBlur}
                    error={meta.touched && meta.error}
                    message={meta.touched && meta.error}
                    required
                  />
                )}
              </Field>
            </div>
            <div className="col-span-6">
              <Field name={`UPI_ID`} >
                {({ field, form, meta }) => (
                  <Input
                    type="text"
                    label="UPI ID"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter UPI ID"
                    name={field.name}
                    onBlur={field.onBlur}
                  // error={meta.touched && meta.error}
                  // message={meta.touched && meta.error}
                  // required
                  />
                )}
              </Field>
            </div>
          </div>
          <div className="flex justify-between items-center gap-3">
            <Field name={`CITY_ID`}>
              {({ field, form, meta }) => (
                <DropDown
                  getOptionValue={(option: City) =>
                    option.CITY_ID
                  }
                  getOptionLabel={(option: City) =>
                    `${option.NAME}`
                  }
                  options={cityList}
                  placeholder="Select City"
                  onChange={(item: City) => {
                    form.setFieldValue(
                      `CITY_ID`,
                      item.CITY_ID,
                    );
                  }}
                  label={'City'}
                  defaultValue={cityList?.find(
                    (item: any) =>
                      item.CITY_ID ===
                      formikProps.values?.CITY_ID,
                  )}
                  name={`CITY_ID`}
                  onBlur={field.onBlur}
                  error={
                    formikProps.touched.CITY_ID && formikProps.errors.CITY_ID
                  }
                  message={
                    formikProps.touched.CITY_ID && formikProps.errors.CITY_ID
                  }
                  required
                />
              )}
            </Field>
            <Field name={`PINCODE`}>
              {({ field, form, meta }) => (
                <Input
                  type="text"
                  label="Pincode"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter Pincode"
                  name={field.name}
                  onBlur={field.onBlur}
                  error={meta.touched && meta.error}
                  message={meta.touched && meta.error}
                  allowOnlyNumbers={true}
                  required
                />
              )}
            </Field>
          </div>

          <div className="flex justify-between items-center gap-3">
            <Field name={`SMS_BRANCH_NAME`}>
              {({ field, form, meta }) => (
                <Input
                  type="text"
                  label="SMS Branch Name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter SMS Branch Name"
                  name={field.name}
                  onBlur={field.onBlur}
                  error={meta.touched && meta.error}
                  message={meta.touched && meta.error}
                  required
                />
              )}
            </Field>
              <Field name={`FOUNDING_YEAR`} >
              {({ field, form, meta }) => (
                <Input
                  type="number"
                  label="Founding Year"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter founding year"
                  name={field.name}
                  onBlur={field.onBlur}
                  error={meta.touched && meta.error}
                  message={meta.touched && meta.error}
                  required
                />
              )}
            </Field>
          </div>
          <div className="flex justify-between items-center gap-3">
            <MapField
              mapField
              name="ADDRESS"
              mapName="GOOGLE_MAP_POSITION"
              backgroundColor="white"
              initialValue={formikProps?.initialValues?.ADDRESS}
              // label="Location"
              placeHolder="Enter Address"
              latitudeName="LATITUDE"
              longitudeName="LONGITUDE"
              geofenceRadiusName="GEOFENCE_RADIUS_METERS"
            />
            {/* <Field name={`ADDRESS`}>
              {({ field, form, meta }) => (
                <Input
                  type="text"
                  label="Address"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter Address"
                  name={field.name}
                  onBlur={field.onBlur}
                  error={meta.touched && meta.error}
                  message={meta.touched && meta.error}
                  required
                />
              )}
            </Field> */}
          </div>

          {/*  */}
          <div className="flex justify-between items-center gap-3">
            <Field name={`CONVENIENCE_FEE_TYPE`}>
              {({ field, form, meta }) => (
                <DropDown
                  // isDisabled={true}
                  label="Convenience Fee Type"
                  name={`CONVENIENCE_FEE_TYPE`}
                  getOptionValue={item => item.value}
                  getOptionLabel={item => item.value}
                  options={[
                    { value: "FLAT" },
                    { value: "PERCENTAGE" },
                  ]}
                  onChange={(item) => {
                    form.setFieldValue(
                      `CONVENIENCE_FEE_TYPE`,
                      item.value,
                    );
                  }}
                  defaultValue={[
                    { value: "FLAT" },
                    { value: "PERCENTAGE" },
                  ]?.find(item => item.value === formikProps.values?.CONVENIENCE_FEE_TYPE)}
                  placeholder="Select Convenience Fee Type"
                  error={
                    formikProps.touched.CONVENIENCE_FEE_TYPE && formikProps.errors.CONVENIENCE_FEE_TYPE
                  }
                  message={
                    formikProps.touched.CONVENIENCE_FEE_TYPE && formikProps.errors.CONVENIENCE_FEE_TYPE
                  }
                  required
                />
              )}
            </Field>
            <Field name={`CONVENIENCE_FEE_RATE`} >
              {({ field, form, meta }) => (
                <Input
                  // disabled={true}
                  type="number"
                  label="Convenience Fee Rate"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter Convenience Fee Rate"
                  name={field.name}
                  onBlur={field.onBlur}
                // error={meta.touched && meta.error}
                // message={meta.touched && meta.error}
                // required
                />
              )}
            </Field>
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        <Button
          isPreviousYearCheckNotRequied={true}
          submit={true}
          type="primary"
          label={props.action ? 'Update' : 'Submit'}
          width="mb-[2vh] mt-[2vw]"
        />
      </div>

    </Form>}
  </Formik> : <></>;
});
